<template>
  <li class="relative" :class="[enlargedMode ? enlargedContainerClass : tileContainerClass]">
    <!-- Stream Device Icon -->
    <div
      class="
        bg-vice-teal-600
        rounded-full
        w-10
        h-10
        content-center
        justify-center
        absolute
        top-2
        left-2
        z-10
        tooltip
      "
    >
      <div class="flex justify-center">
        <ViceGlasses class="h-10 w-10" />
      </div>
      <span class="tooltip-text ml-2">Smartglasses</span>
    </div>

    <!-- Mute -->
    <ToggleMuteStream
      v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION"
      @stream-state-toggled="toggleStreamState"
      :isStreamMuted="streamMuted"
    />

    <div class="group block w-full h-full relative rounded-xl overflow-visible">
      <div class="group block h-full relative rounded-xl bg-black">
        <!-- Icon and trigger -->
        <div class="absolute top-0 right-0 z-10" v-if="isConsultationPhase">
          <div class="p-3 relative cursor-pointer" :class="{ 'sm:p-1': !enlargedMode }">
            <div class="relative flex items-center gap-2">
              <div
                data-cy="toggle-focus"
                :class="{ 'sm:p-1': !enlargedMode }"
                class="
                  bg-vice-dark-grey bg-opacity-70
                  text-white
                  rounded-full
                  flex
                  justify-center
                  items-center
                  p-2
                  hover:bg-opacity-90
                "
              >
                <ArrowsExpandIcon
                  class="h-6 w-6"
                  v-if="!enlargedMode"
                  :class="{ 'sm:w-5 sm:h-5': !enlargedMode }"
                  @click="onEnlargeIconClick(tileId)"
                />
                <ArrowsCollapseIcon class="p-0.5" @click="onMinimizeIconClick" v-else />
              </div>
            </div>
          </div>
        </div>

        <StreamPlayerRemote
          :streamMuted="streamMuted"
          :isAudioMuted="isAudioMuted"
          :jitsiUserId="userJitsiId"
          :setVideoAudioRefs="setVideoAudioRefs"
          :showVideo="shouldShowVuzixVideo"
        />

        <span
          v-if="streamMuted"
          class="muted-text w-full items-center justify-center rounded-xl z-5"
        >
          PAUSED
        </span>

        <div
          class="flex justify-center items-center h-full w-full bg-black"
          v-show="shouldShowVuzixVideoOff"
        >
          <VideoOff
            :name="remoteDisplayInfo?.['name']"
            :profile_photo_url="remoteDisplayInfo?.['photo']"
            :showImage="shouldDisplayVuzixStream"
            displayText="Stream preview is hidden"
          />
        </div>

        <UserLabel
          :class="{ 'text-xxs': enlargedJitsiId }"
          :isMutedMicrophone="!remoteDisplayInfo?.['audio'] || sessionStore.isPutOnHold"
          :isAudioMuted="isAudioMuted"
          :externalAudioIO="externalAudioIO"
          displayName="Smartglasses"
          :localStats="remoteDisplayInfo?.['connectionQuality']"
          @togglePreviewOverlay="toggleVuzixStream"
          @toggleVuzixAudio="toggleVuzixAudio"
          @toggleExternalAudioIO="toggleExternalAudioIO"
          @disconnectDevice="disconnectSmartglasses"
          :showDisconnectDevice="true"
          :isMultiCameraStream="true"
        />
      </div>
    </div>
    <SpeakingIndicator :audioLevel="remoteDisplayInfo.audioLevel" />
  </li>
</template>

<script>
import { computed, ref } from 'vue';
import { ArrowsExpandIcon } from '@heroicons/vue/outline';
import VideoOff from '@/Components/Session/Grid/VideoOff.vue';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';
import useSessionStore from '@/Stores/useSessionStore';
import StreamPlayerRemote from '@/Components/Session/Grid/StreamPlayerRemote.vue';
import ArrowsCollapseIcon from '@/Components/Icons/ArrowsCollapseIcon.vue';
import { VICE_SESSION_PHASES, DISPLAY_NAME } from '@/Utils/vice-constants';
import ToggleMuteStream from '@/Components/Inputs/ToggleMuteStream.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import SpeakingIndicator from '@/Components/Session/Grid/SpeakingIndicator.vue';
import ViceGlasses from '@/Components/Icons/ViceGlasses.vue';

export default {
  computed: {
    VICE_SESSION_PHASES() {
      return VICE_SESSION_PHASES;
    },
  },
  props: {
    index: {
      type: Number,
      required: true,
    },
    userJitsiId: {
      type: [Number, String],
    },
    enlargedJitsiId: {
      type: String,
    },
    remoteDisplayInfo: {
      type: Object,
      required: true,
    },
    muteMedia: {
      type: Function,
    },
    setVideoAudioRefs: {
      type: Function,
      required: true,
    },
    tileId: {
      type: String,
      required: false,
    },
    gridDimension: {
      type: Object,
      required: true,
    },
  },
  emits: ['toggleEnlargeMode'],
  components: {
    ViceGlasses,
    VideoOff,
    UserLabel,
    ToggleMuteStream,
    ArrowsExpandIcon,
    SpeakingIndicator,
    ArrowsCollapseIcon,
    StreamPlayerRemote,
  },
  setup(props, { emit }) {
    const streamMuted = ref(false);
    const sessionStore = useSessionStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const isAudioMuted = ref(true);
    const shouldDisplayVuzixStream = ref(true);

    const externalAudioIO = computed(() => multiCameraDevicesStore.externalAudioIO);
    const shouldShowVuzixVideo = computed(
      () =>
        props.remoteDisplayInfo?.video &&
        shouldDisplayVuzixStream.value &&
        !sessionStore.isPhasePaused,
    );

    const shouldShowVuzixVideoOff = computed(
      () =>
        !props.remoteDisplayInfo?.video ||
        !shouldDisplayVuzixStream.value ||
        sessionStore.isPhasePaused,
    );

    const disconnectSmartglasses = () => {
      sessionStore.deviceMessageSender.turnOffSmartGlass();
      multiCameraDevicesStore.removeExternalDevicePreview(DISPLAY_NAME.VUZIX);
    };

    const toggleVuzixAudio = () => {
      isAudioMuted.value = !isAudioMuted.value;
      sessionStore.deviceMessageSender.toggleSmartglassAudioIO(isAudioMuted.value);
    };

    // Toggle using SG as audio input/output instead of the PC audio
    const toggleExternalAudioIO = () => {
      // If the current audio state does not match the desired state - toggle it
      if (multiCameraDevicesStore.externalAudioIO !== isAudioMuted.value) toggleVuzixAudio();
      multiCameraDevicesStore.externalAudioIO = !multiCameraDevicesStore.externalAudioIO;
    };

    const toggleVuzixStream = () => {
      shouldDisplayVuzixStream.value = !shouldDisplayVuzixStream.value;
    };

    const tileContainerClass = computed(
      () =>
        `col-start-${props.gridDimension[props.index].start ?? 1} col-span-${
          props.gridDimension[props.index].cols
        } row-span-${props.gridDimension[props.index].rows}`,
    );
    const enlargedContainerClass = 'col-start-2 col-span-10 row-span-8 row-start-1';
    const enlargedMode = computed(() => props.enlargedJitsiId === props.userJitsiId);

    const isConsultationPhase = ref(sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION);

    const onEnlargeIconClick = () => {
      emit('toggleEnlargeMode', props.userJitsiId);
    };

    const onMinimizeIconClick = () => {
      emit('toggleEnlargeMode');
    };

    const toggleStreamState = (e) => {
      streamMuted.value = e;
      props.muteMedia(props.userJitsiId, e);
    };

    return {
      streamMuted,
      sessionStore,
      isAudioMuted,
      enlargedMode,
      externalAudioIO,
      tileContainerClass,
      isConsultationPhase,
      shouldShowVuzixVideo,
      enlargedContainerClass,
      shouldShowVuzixVideoOff,
      shouldDisplayVuzixStream,
      toggleVuzixAudio,
      toggleVuzixStream,
      toggleStreamState,
      onEnlargeIconClick,
      onMinimizeIconClick,
      toggleExternalAudioIO,
      disconnectSmartglasses,
    };
  },
};
</script>
<style scoped>
.hide {
  display: none;
}
</style>
